import React, { useState, memo } from 'react';
import { Edit2, X, MoreHorizontal } from 'lucide-react';
import { AppNode, NodeType } from '@/types';

interface NodeHeaderProps {
  node: AppNode;
  onUpdate?: (id: string, data: any, size?: any, title?: string) => void;
  onDelete?: (id: string) => void;
}

const NODE_TYPE_NAMES: Record<NodeType, string> = {
  [NodeType.PROMPT_INPUT]: '创意描述',
  [NodeType.MEDIA_UPLOAD]: '媒体上传',
  [NodeType.IMAGE_GENERATOR]: '图片生成',
  [NodeType.VIDEO_GENERATOR]: '视频生成',
  [NodeType.IMAGE_EDITOR]: '图像编辑',
  [NodeType.AUDIO_GENERATOR]: '灵感音乐',
  [NodeType.TEXT_NODE]: '文本节点',
  [NodeType.SCRIPT_NODE]: '脚本生成器',
  [NodeType.VIDEO_COMPOSE]: '智能剪辑',
  [NodeType.PANORAMA_GENERATOR]: '720°全景图',
  [NodeType.DIRECTOR]: '导演台',
  [NodeType.DETAIL_PAGE]: '详情页',
  [NodeType.LAYER_EDIT]: '图层编辑',
  [NodeType.GROUP]: '分组',
};

const NodeHeaderInner: React.FC<NodeHeaderProps> = ({
  node,
  onUpdate,
  onDelete,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempTitle, setTempTitle] = useState(node.title);

  const handleTitleSubmit = () => {
    setIsEditing(false);
    if (tempTitle !== node.title && onUpdate) {
      onUpdate(node.id, {}, undefined, tempTitle);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSubmit();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setTempTitle(node.title);
    }
  };

  return (
    <div className="flex items-center justify-between px-3 py-2 bg-[#262626]/50 border-b border-white/10">
      {isEditing ? (
        <input
          type="text"
          value={tempTitle}
          onChange={(e) => setTempTitle(e.target.value)}
          onBlur={handleTitleSubmit}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent text-xs font-medium text-white outline-none border-b border-cyan-400"
          autoFocus
        />
      ) : (
        <div
          className="flex-1 text-xs font-medium text-white truncate cursor-default hover:text-cyan-400 transition-colors"
          onClick={() => setIsEditing(true)}
        >
          {node.title || NODE_TYPE_NAMES[node.type]}
        </div>
      )}
      <div className="flex items-center gap-1">
        <button
          className="p-1 hover:bg-white/10 rounded transition-colors"
          onClick={() => setIsEditing(true)}
        >
          <Edit2 size={12} className="text-slate-400" />
        </button>
        {onDelete && (
          <button
            className="p-1 hover:bg-red-500/20 rounded transition-colors"
            onClick={() => onDelete(node.id)}
          >
            <X size={12} className="text-red-400" />
          </button>
        )}
      </div>
    </div>
  );
};

export const NodeHeader = memo(NodeHeaderInner, (prevProps, nextProps) => {
  return (
    prevProps.node.id === nextProps.node.id &&
    prevProps.node.title === nextProps.node.title &&
    prevProps.node.type === nextProps.node.type
  );
});